import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
// __EQUIP_COMPAT__ (2026-10-04) every equipment variable per type, in the viewer's units
import EquipmentSpecFields from './EquipmentSpecFields';
import { TRAILER_TYPES, typeInfo, unitCapacity, fmtFt, fmtLb } from '../utils/equipmentCompat';
import { specToForm, formToSpec, applyTypeDefaults, missingSpecs } from '../utils/equipmentForm';
import { getDefaultUnitSystem } from '../utils/units';
import { invalidateEquipmentCache } from '../services/equipmentService';
import { UnitDowntimeEditor } from './equipment/UnitDowntime';   // __UNIT_DOWNTIME__

const EMPTY_TRAILER = {
  trailer_number: '',
  license_plate: '',
  vin: '',
  make: '',
  model: '',
  year: '',
  trailer_type: 'flatbed',
  axle_count: 2,
  status: 'available',
  is_active: true,
  notes: ''
};

function TrailerManagementModal({ onClose, onSuccess }) {
  const [trailers, setTrailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTrailer, setEditingTrailer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const units = getDefaultUnitSystem();
  const [formData, setFormData] = useState({ ...EMPTY_TRAILER });
  const [spec, setSpec] = useState(() => applyTypeDefaults(specToForm(null, units), 'trailer', 'flatbed', units));

  useEffect(() => {
    fetchTrailers();
  }, []);

  const fetchTrailers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('trailers')
        .select('*')
        .order('trailer_number', { ascending: true });

      if (error) throw error;
      setTrailers(data || []);
    } catch (err) {
      console.error('Error fetching trailers:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...EMPTY_TRAILER });
    setSpec(applyTypeDefaults(specToForm(null, units), 'trailer', 'flatbed', units));
    setEditingTrailer(null);
    setError(null);
  };

  const handleEdit = (trailer) => {
    setFormData({
      trailer_number: trailer.trailer_number || '',
      license_plate: trailer.license_plate || '',
      vin: trailer.vin || '',
      make: trailer.make || '',
      model: trailer.model || '',
      year: trailer.year || '',
      trailer_type: trailer.trailer_type || 'flatbed',
      axle_count: trailer.axle_count || 2,
      status: trailer.status || 'available',
      is_active: trailer.is_active !== false,
      notes: trailer.notes || ''
    });
    setSpec(specToForm(trailer, units));
    setEditingTrailer(trailer);
    setShowForm(true);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (!formData.trailer_number.trim()) {
        throw new Error('Trailer number is required');
      }

      const trailerData = {
        trailer_number: formData.trailer_number.trim(),
        license_plate: formData.license_plate || null,
        vin: formData.vin || null,
        make: formData.make || null,
        model: formData.model || null,
        trailer_type: formData.trailer_type,
        status: formData.status,
        is_active: formData.is_active !== false,
        notes: formData.notes || null,
        year: formData.year ? parseInt(formData.year, 10) : null,
        axle_count: formData.axle_count ? parseInt(formData.axle_count, 10) : 2,
        ...formToSpec(spec, 'trailer', units)
      };

      if (editingTrailer) {
        const { error } = await supabase
          .from('trailers')
          .update(trailerData)
          .eq('id', editingTrailer.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('trailers')
          .insert([trailerData]);

        if (error) throw error;
      }

      invalidateEquipmentCache();
      await fetchTrailers();
      resetForm();
      setShowForm(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('Error saving trailer:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (trailer) => {
    if (deleteConfirmId !== trailer.id) { setDeleteConfirmId(trailer.id); return; }
    setDeleteConfirmId(null);

    try {
      const { error } = await supabase
        .from('trailers')
        .delete()
        .eq('id', trailer.id);

      if (error) throw error;
      await fetchTrailers();
    } catch (err) {
      console.error('Error deleting trailer:', err);
      setError(err.message);
    }
  };

  const filteredTrailers = trailers.filter(t => 
    t.trailer_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.license_plate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.make?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.trailer_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'var(--color-success)';
      case 'in_use': return 'var(--accent-blue)';
      case 'maintenance': return 'var(--color-warning)';
      case 'out_of_service': return 'var(--color-danger)';
      default: return 'var(--text-muted)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'available': return 'Available';
      case 'in_use': return 'In Use';
      case 'maintenance': return 'Maintenance';
      case 'out_of_service': return 'Out of Service';
      default: return status;
    }
  };

  const getTrailerTypeLabel = (type) => {
    const types = {
      'flatbed': 'Flatbed',
      'dry_van': 'Dry Van',
      'reefer': 'Reefer',
      'tanker': 'Tanker',
      'lowboy': 'Lowboy',
      'step_deck': 'Step Deck',
      'dump': 'Dump',
      'hopper': 'Hopper',
      'livestock': 'Livestock',
      'car_hauler': 'Car Hauler',
      'other': 'Other'
    };
    return types[type] || type;
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem',
    backgroundColor: 'var(--surface-2)',
    border: '1px solid var(--border-default)',
    borderRadius: '5px',
    color: 'var(--text-primary)',
    fontSize: '1rem'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '0.9rem'
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        backgroundColor: 'var(--surface-1)',
        borderRadius: '10px',
        padding: '2rem',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--surface-2)'
        }}>
          <h2 style={{ color: 'var(--accent-primary)', margin: 0 }}>
            {showForm ? (editingTrailer ? 'Edit Trailer' : 'Add Trailer') : 'Manage Trailers'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-primary)',
              fontSize: '1.5rem',
              cursor: 'pointer'
            }}
          >
            x
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--color-danger)',
            color: 'var(--text-primary)',
            padding: '1rem',
            borderRadius: '5px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {showForm ? (
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <div>
                <label style={labelStyle}>Trailer Number *</label>
                <input
                  type="text"
                  value={formData.trailer_number}
                  onChange={(e) => setFormData({ ...formData, trailer_number: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., TR-001"
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>License Plate</label>
                <input
                  type="text"
                  value={formData.license_plate}
                  onChange={(e) => setFormData({ ...formData, license_plate: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., ABC 1234"
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>VIN</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value })}
                  style={inputStyle}
                  placeholder="Vehicle Identification Number"
                />
              </div>

              <div>
                <label style={labelStyle}>Make</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., Great Dane"
                />
              </div>

              <div>
                <label style={labelStyle}>Model</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., Freedom LT"
                />
              </div>

              <div>
                <label style={labelStyle}>Year</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., 2022"
                  min="1990"
                  max="2030"
                />
              </div>

              <div>
                <label style={labelStyle}>Trailer Type</label>
                <select
                  value={formData.trailer_type}
                  onChange={(e) => {
                    setFormData({ ...formData, trailer_type: e.target.value });
                    if (!editingTrailer) setSpec(applyTypeDefaults({ ...spec, deckHeight: '', features: [] }, 'trailer', e.target.value, units));
                  }}
                  style={inputStyle}
                >
                  {Object.entries(TRAILER_TYPES).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
                  {formData.trailer_type && !TRAILER_TYPES[formData.trailer_type] && <option value={formData.trailer_type}>{formData.trailer_type}</option>}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Axle Count</label>
                <input
                  type="number"
                  value={formData.axle_count}
                  onChange={(e) => setFormData({ ...formData, axle_count: e.target.value })}
                  style={inputStyle}
                  placeholder="e.g., 2"
                  min="1"
                  max="10"
                />
              </div>

              <div>
                <label style={labelStyle}>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  style={inputStyle}
                >
                  <option value="available">Available</option>
                  <option value="in_use">In Use</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.4rem', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                  <input type="checkbox" checked={formData.is_active !== false} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />
                  Active (untick to retire it - kept for history)
                </label>
              </div>

              {/* __EQUIP_COMPAT__ weights / deck / features for this type */}
              <EquipmentSpecFields kind="trailer" type={formData.trailer_type} spec={spec} setSpec={setSpec} units={units}
                inputStyle={inputStyle} labelStyle={labelStyle} />

              {/* __UNIT_DOWNTIME__ dated maintenance / out-of-service days (an existing trailer) */}
              {editingTrailer && <UnitDowntimeEditor kind="trailer" unitId={editingTrailer.id} />}

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                  placeholder="Any additional notes about this trailer..."
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { resetForm(); setShowForm(false); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--surface-3)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: saving ? 'var(--surface-3)' : 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {saving ? 'Saving...' : (editingTrailer ? 'Update Trailer' : 'Add Trailer')}
              </button>
            </div>
          </form>
        ) : (
          <>
            <div style={{
              display: 'flex',
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
              <input
                type="text"
                placeholder="Search trailers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ ...inputStyle, flex: 1 }}
              />
              <button
                onClick={() => { resetForm(); setShowForm(true); }}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--accent-primary)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap'
                }}
              >
                + Add Trailer
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                Loading trailers...
              </div>
            ) : filteredTrailers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                {searchTerm ? 'No trailers match your search' : 'No trailers yet. Add one to get started!'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '0.75rem',
                maxHeight: '400px',
                overflowY: 'auto'
              }}>
                {filteredTrailers.map((trailer) => (
                  <div
                    key={trailer.id}
                    style={{
                      backgroundColor: 'var(--surface-2)',
                      padding: '1rem',
                      borderRadius: '5px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {trailer.trailer_number}
                        </span>
                        <span style={{
                          padding: '0.2rem 0.5rem',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          backgroundColor: 'var(--color-info)',
                          color: 'var(--text-primary)'
                        }}>
                          {getTrailerTypeLabel(trailer.trailer_type)}
                        </span>
                        <span style={{
                          padding: '0.2rem 0.5rem',
                          borderRadius: '3px',
                          fontSize: '0.75rem',
                          backgroundColor: getStatusColor(trailer.status),
                          color: 'var(--text-primary)'
                        }}>
                          {getStatusLabel(trailer.status)}
                        </span>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {trailer.make && trailer.model && (
                          <span>{trailer.year ? `${trailer.year} ` : ''}{trailer.make} {trailer.model}</span>
                        )}
                        {trailer.license_plate && (
                          <span style={{ marginLeft: trailer.make ? '1rem' : 0 }}>
                            Plate: {trailer.license_plate}
                          </span>
                        )}
                      </div>
                      {(() => {
                        // __EQUIP_COMPAT__ type, what the check will use, and what is still missing
                        const cap = unitCapacity({ truck: { truck_type: 'day_cab' }, trailer, combo: null });
                        const miss = missingSpecs(trailer, 'trailer');
                        return (
                          <div style={{ fontSize: '0.8rem', marginTop: '0.25rem' }}>
                            <span style={{ color: 'var(--text-secondary)' }}>{typeInfo('trailer', trailer.trailer_type).label}</span>
                            {trailer.is_active === false && <span style={{ color: 'var(--color-danger)', marginLeft: '0.5rem' }}>RETIRED</span>}
                            {cap && cap.payloadLb != null && <span style={{ color: 'var(--text-muted)' }}> | payload {fmtLb(cap.payloadLb, units)}</span>}
                            {cap && cap.deckLengthFt != null && <span style={{ color: 'var(--text-muted)' }}> | deck {fmtFt(cap.deckLengthFt, units)} x {fmtFt(cap.deckWidthFt, units)}</span>}
                            {(trailer.features || []).length > 0 && <span style={{ color: 'var(--text-muted)' }}> | {(trailer.features || []).join(', ')}</span>}
                            {miss.length > 0 && <div style={{ color: 'var(--color-warning)' }}>Missing for the equipment check: {miss.join(', ')}</div>}
                          </div>
                        );
                      })()}
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleEdit(trailer)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--accent-blue)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Edit
                      </button>
                      {deleteConfirmId === trailer.id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Sure?</span>
                          <button onClick={() => handleDelete(trailer)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Yes</button>
                          <button onClick={() => setDeleteConfirmId(null)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirmId(trailer.id)} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Delete</button>
                      )}  
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{
              marginTop: '1rem',
              paddingTop: '1rem',
              borderTop: '1px solid var(--surface-2)',
              color: 'var(--text-muted)',
              fontSize: '0.9rem',
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <span>
                {filteredTrailers.length} trailer{filteredTrailers.length !== 1 ? 's' : ''} 
                {searchTerm && ` matching "${searchTerm}"`}
              </span>
              <span>
                {trailers.filter(t => t.status === 'available').length} available | 
                {trailers.filter(t => t.status === 'in_use').length} in use | 
                {trailers.filter(t => t.status === 'maintenance').length} maintenance
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TrailerManagementModal;
